import React, { useEffect, useState } from 'react'
import { ROUTES } from './helpers/routes'
import { Route, Switch } from 'react-router-dom'
import { Dashboard } from './components/pages/Dashboard/Dashboard'
import { completeContainer } from '../styles/shared'
import { FollowDrawer } from '@csinstruments/cs-react-theme'
import { LicenseServerNavigation } from './components/partials/Navigation/LicenseServerNavigation'
import LicenseServerAppTop from './components/partials/Navigation/LicenseServerAppTop'
import { PageContent } from './components/partials/PageContent'
import { GrantOrUpdateLicense } from './components/pages/Licenses/GrantOrUpdateLicense'
import { CreateOrEditContact } from './components/pages/Contacts/CreateOrEditContact'
import { ListContacts } from './components/pages/Contacts/ListContacts'
import { CreateOrUpdateLicenseType } from './components/pages/LicenseType/CreateOrUpdateLicenseType'
import { ListLicenseTypes } from './components/pages/LicenseType/ListLicenseTypes'
import { ListLicensePools } from './components/pages/LicensePools/ListLicensePools'
import { CreateOrUpdateLicensePool } from './components/pages/LicensePools/CreateOrUpdateLicensePool'
import { ListLicenses } from './components/pages/Licenses/ListLicenses'
import { Journal } from './components/pages/Journal/Journal'
import { ActivateLicense } from './components/pages/ActivateLicense'
import { LicenseActivations } from './components/pages/LicenseActivations'
import { useKeycloak } from '@react-keycloak/web'
import { BatchGrantLicenses } from './components/pages/Licenses/BatchGrantLicense'
import { getTokens, hasUserRoles } from './helpers/keycloakService'
import { Users } from './components/pages/Users'
import { GroupsOverview } from './components/pages/Groups'
import { ApolloProvider } from '@apollo/client'
import { getApolloClient } from './helpers/apolloClient'
import AccessDenied from './components/partials/AccessDenied'
import { ListLicenseInstances } from './components/pages/Licenses/ListLicenseInstances'
import { CookiesProvider } from 'react-cookie'
import { ActivateLicenseServer } from './components/pages/ActivateLicenseServer'
import { DeactivateLicenseServer } from './components/pages/DeactivateLicenseServer'

function App(): React.ReactElement {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(true)
  const { initialized } = useKeycloak()
  const [tokensAvailable, setTokensAvailable] = useState(false)
  const [token, setToken] = useState('')

  useEffect(() => {
    const [token] = getTokens()
    if (!token) {
      // wait for local storage
      setTimeout(() => {
        const [token] = getTokens()
        if (token) {
          setTokensAvailable(true)
          setToken(token)
        }
      }, 500)
    } else {
      setTokensAvailable(true)
      setToken(token)
    }
  }, [])

  if (!initialized || !tokensAvailable) {
    return <></>
  }

  const whiteListRoutesForLicenseCustomer = (): boolean => {
    return (
      window.location.pathname.includes(ROUTES.activateLicense) ||
      window.location.pathname.includes(ROUTES.licenseActivations) ||
      window.location.pathname.includes(ROUTES.activateLicenseV1)
    )
  }

  // no access to app for users without license access configured in keycloak. exception: activateLicense can done by everyone
  if (!hasUserRoles(token).anyRole && !whiteListRoutesForLicenseCustomer()) {
    return <AccessDenied />
  }

  const apolloClient = getApolloClient()

  return (
    <>
      <CookiesProvider>
        <ApolloProvider client={apolloClient}>
          <Switch>
            <Route exact path={ROUTES.activateLicense}>
              <ActivateLicense />
            </Route>
            <Route exact path={ROUTES.activateLicenseV1}>
              <ActivateLicense />
            </Route>
            <Route exact path={ROUTES.activateLicenseLicenseServerV1}>
              <ActivateLicenseServer />
            </Route>
            <Route exact path={ROUTES.deactivateLicenseLicenseServerV1}>
              <DeactivateLicenseServer />
            </Route>
            <Route exact path={ROUTES.licenseActivations} render={() => <LicenseActivations />} />
            <Route>
              <div style={completeContainer}>
                <LicenseServerNavigation onDrawerAction={(open) => setDrawerOpen(open)} />
                <FollowDrawer open={drawerOpen}>
                  <LicenseServerAppTop />
                  <PageContent>
                    <Switch>
                      <Route exact path={ROUTES.grantLicense} render={() => <GrantOrUpdateLicense />} />
                      <Route exact path={ROUTES.grantLicenseForPool} render={() => <GrantOrUpdateLicense />} />
                      <Route exact path={ROUTES.batchGrantLicense} render={() => <BatchGrantLicenses />} />
                      <Route exact path={ROUTES.editLicense} render={() => <GrantOrUpdateLicense />} />
                      <Route exact path={ROUTES.home} render={() => <Dashboard />} />
                      <Route exact path={ROUTES.addCustomer} render={() => <CreateOrEditContact />} />
                      <Route exact path={ROUTES.addMerchant} render={() => <CreateOrEditContact />} />
                      <Route exact path={ROUTES.editCustomer} render={() => <CreateOrEditContact />} />
                      <Route exact path={ROUTES.editMerchant} render={() => <CreateOrEditContact />} />
                      <Route exact path={ROUTES.editLicensePool} render={() => <CreateOrUpdateLicensePool />} />
                      <Route exact path={ROUTES.merchantsCustomers} render={() => <ListContacts />} />
                      <Route exact path={ROUTES.merchants} render={() => <ListContacts />} />
                      <Route exact path={ROUTES.customers} render={() => <ListContacts />} />
                      <Route exact path={ROUTES.addLicenseType} render={() => <CreateOrUpdateLicenseType />} />
                      <Route exact path={ROUTES.editLicenseType} render={() => <CreateOrUpdateLicenseType />} />
                      <Route exact path={ROUTES.licenseTypes} render={() => <ListLicenseTypes />} />
                      <Route exact path={ROUTES.licenseInstances} render={() => <ListLicenseInstances />} />
                      <Route exact path={ROUTES.licensePools} render={() => <ListLicensePools />} />
                      <Route exact path={ROUTES.addLicensePool} render={() => <CreateOrUpdateLicensePool />} />
                      <Route exact path={ROUTES.addLicensePoolForGroup} render={() => <CreateOrUpdateLicensePool />} />
                      <Route exact path={ROUTES.addLicensePoolForUser} render={() => <CreateOrUpdateLicensePool />} />
                      <Route exact path={ROUTES.licenses} render={() => <ListLicenses />} />
                      <Route exact path={ROUTES.journal} render={() => <Journal />} />
                      <Route exact path={ROUTES.users} render={() => <Users />} />
                      <Route exact path={ROUTES.groupUsers} render={() => <Users />} />
                      <Route exact path={ROUTES.groups} render={() => <GroupsOverview />} />
                      <Route render={() => <Dashboard />} />
                    </Switch>
                  </PageContent>
                </FollowDrawer>
              </div>
            </Route>
          </Switch>
        </ApolloProvider>
      </CookiesProvider>
    </>
  )
}

export default App
